import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ErrorIcon from '@mui/icons-material/Error';

import { TableCell } from 'common/components/Table';
import { Chip, ChipGroup } from 'common/components/Chip';
import { IGroupManagementListItemFragment } from 'graphql/types';
import { getUrl } from 'route/utils/getUrl';
import { Checkbox } from 'common/components/Checkbox';
import { ExtraCategoryLabels } from 'organisation/components/ExtraCategoryLabels';

interface IProps {
    group: IGroupManagementListItemFragment;
    isSelected: boolean;
    onSelect?: () => void;
}

export const GroupsManagementTableRow = ({
    group,
    isSelected,
    onSelect,
}: IProps) => {
    const [translate] = useTranslation();
    const navigate = useNavigate();

    const {
        id,
        compliancy,
        isEditable,
        name,
        userCount,
        managerCount,
        extraCategoryValues,
    } = group;

    return (
        <TableRow
            hover
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate(getUrl('GROUP_EDIT', { id }))}
        >
            <TableCell>
                <Checkbox
                    checked={isSelected}
                    disabled={!isEditable}
                    onClick={(e) => {
                        onSelect?.();

                        e.stopPropagation();
                    }}
                />
            </TableCell>

            <TableCell>
                {name}

                <ChipGroup sx={{ my: 0.25 }}>
                    {!!userCount && (
                        <Chip
                            label={translate('userCount', {
                                count: userCount,
                            })}
                            size="small"
                        />
                    )}

                    {!!managerCount && (
                        <Chip
                            label={translate('managerCount', {
                                count: managerCount,
                            })}
                            size="small"
                        />
                    )}
                    {!!extraCategoryValues?.length && (
                        <ExtraCategoryLabels
                            chipProps={{ size: 'small' }}
                            extraCategoryValues={extraCategoryValues}
                        />
                    )}
                </ChipGroup>
            </TableCell>

            <TableCell sx={{ textAlign: 'right' }}>
                {compliancy?.isCompliant === false && (
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Typography
                            sx={{ color: 'text.secondary' }}
                            variant="body2"
                        >
                            {translate(
                                'groupsManagementPage.compliancyStatus.minimumNotMet'
                            )}
                        </Typography>

                        <ErrorIcon
                            aria-hidden="true"
                            sx={{ color: 'error.main', fontSize: 20 }}
                        />
                    </Box>
                )}
            </TableCell>
        </TableRow>
    );
};
